import { Card, CardContent, Typography, Box, IconButton } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useRef, useState } from "react";
import { useGetChangePaymentCardFormDataMutation } from "@features/userDetails/userDetailsApi.ts";
import useLocale from "@utils/useLocale.ts";
import {
  GetChangePaymentCardFormDataBody,
  PaymentCard as PaymentCardType,
} from "@features/userDetails/types.ts";
import { useTranslation } from "react-i18next";

const PaymentCard = ({ paymentCard }: { paymentCard: PaymentCardType }) => {
  const { t } = useTranslation();
  const [getFormData, { data, isLoading, isSuccess }] =
    useGetChangePaymentCardFormDataMutation();
  const formRef = useRef<HTMLFormElement>(null);
  const { locale } = useLocale();

  const handleClick = () => {
    void getFormData(undefined);
  };

  const [formBody, setFormBody] = useState<GetChangePaymentCardFormDataBody>({
    language: locale.toUpperCase(),
    ["checkout-redirect-success-url"]: "",
    ["checkout-redirect-cancel-url"]: "",
    ["checkout-account"]: "",
    ["checkout-algorithm"]: "",
    ["checkout-method"]: "",
    ["checkout-nonce"]: "",
    ["checkout-timestamp"]: "",
    signature: "",
  });

  useEffect(() => {
    if (isSuccess) {
      setFormBody(data.body);
    }
  }, [isSuccess, data?.body]);

  useEffect(() => {
    if (formBody.signature) {
      formRef.current?.submit();
    }
  }, [formBody.signature]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 4 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          {t("userDetailsPage.paymentCard.paymentMethod")}
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <CreditCardIcon color="action" />
            <Box>
              <Typography variant="body1" fontWeight="bold">
                {paymentCard.cardPreview}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {paymentCard.expiryMonth &&
                  paymentCard.expiryYear &&
                  t("userDetailsPage.paymentCard.expiryText", {
                    month: paymentCard.expiryMonth,
                    year: paymentCard.expiryYear,
                  })}
              </Typography>
            </Box>
          </Box>
          <form
            ref={formRef}
            action={data?.url}
            method="POST"
            style={{ display: "none" }}
          >
            <input type="hidden" name="language" value={formBody.language} />
            <input
              type="hidden"
              name="checkout-redirect-success-url"
              value={formBody["checkout-redirect-success-url"]}
            />
            <input
              type="hidden"
              name="checkout-redirect-cancel-url"
              value={formBody["checkout-redirect-cancel-url"]}
            />
            <input
              type="hidden"
              name="checkout-account"
              value={formBody["checkout-account"]}
            />
            <input
              type="hidden"
              name="checkout-algorithm"
              value={formBody["checkout-algorithm"]}
            />
            <input
              type="hidden"
              name="checkout-method"
              value={formBody["checkout-method"]}
            />
            <input
              type="hidden"
              name="checkout-nonce"
              value={formBody["checkout-nonce"]}
            />
            <input
              type="hidden"
              name="checkout-timestamp"
              value={formBody["checkout-timestamp"]}
            />
            <input type="hidden" name="signature" value={formBody.signature} />
          </form>
          <IconButton
            size="small"
            onClick={handleClick}
            disabled={isLoading}
            loading={isLoading}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaymentCard;
